import * as Yup from 'yup';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import { Card, Grid, Alert, Skeleton, Typography } from '@mui/material';

import { IntegrationSetting } from 'src/services/integration/integrations.types';
import { useGetIntegrationSettingsQuery, useUpdateIntegrationSettingsMutation } from 'src/services/integration/integrations.service';

import { RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';

// ----------------------------------------------------------------------

type IdibuSettingsProps = {
  integrationId: string;
};

export default function IdibuSettings({
  integrationId
}: IdibuSettingsProps) {

  const { enqueueSnackbar } = useSnackbar();

  const { currentData: integrationSettings, isLoading: isRetrievingSettings } = useGetIntegrationSettingsQuery(
    {
      integrationId: integrationId as string,
    },
    {
      skip: !integrationId
    }
  );

  const [updateSettings, { isLoading: isSubmitting }] = useUpdateIntegrationSettingsMutation();

  const IdibuSettingsSchema = Yup.object().shape({
    webhook_secret: Yup.string().required(t('validation.required'))
  });

  const methods = useForm({
    resolver: yupResolver(IdibuSettingsSchema),
    defaultValues: {
      webhook_secret: '',
    }
  });

  const idibuWebhookUrl = useMemo(() => `${import.meta.env.VITE_BACKEND_URL}/webhooks/idibu/${integrationId}`, [integrationId]);

  const {
    setValue,
    handleSubmit,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    if (!integrationSettings) return;

    const webhookSecretSetting = integrationSettings.find((setting) => setting.setting_type === IntegrationSetting.WEBHOOK_SECRET);

    if (webhookSecretSetting && webhookSecretSetting.setting_value) {
      setValue('webhook_secret', webhookSecretSetting.setting_value as string);
    }

  }, [integrationSettings, setValue]);

  const buildSettingsDto = (data: any) => {
    if (!integrationSettings) return [];

    const settings = [];

    const webhookSecretSetting = integrationSettings.find((setting) => setting.setting_type === IntegrationSetting.WEBHOOK_SECRET);

    if (webhookSecretSetting) {
      settings.push({
        id: webhookSecretSetting.id as string,
        value: data.webhook_secret,
      });
    }

    return settings;
  }

  const onSubmit = handleSubmit(async (data: any) => {
    try {

      const settings = buildSettingsDto(data);

      await updateSettings({
        integrationId: integrationId as string,
        settings
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.update.success'), { variant: 'success' });
    }
    catch (e) {
      enqueueSnackbar(t('admin-organization.settings.update.default_error'), { variant: 'error' });
    };
  })

  return (
    <Card sx={{ py: 2, px: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t('integrations.detail.configuration.settings.title')}</Typography>
      </Box>

      {
        isRetrievingSettings ? (
          <Grid container gap={2} sx={{ px: 2, mt: 2 }}>
            <Grid xs={12}>
              <Skeleton variant="rectangular" height={20} width="50%" />
            </Grid>
            <Grid xs={12}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          </Grid>
        ) :
          (
            <FormProvider methods={methods}>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Alert variant="outlined" severity="info">
                      <Stack>
                        <Typography>
                          {t('integrations.detail.configuration.settings.webhook_url.alert')}
                        </Typography>
                        <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
                          {idibuWebhookUrl}
                        </Typography>
                      </Stack>
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <RHFTextField
                      name="webhook_secret"
                      label={t('integrations.detail.configuration.settings.webhook_secret.label')}
                      placeholder={t('integrations.detail.configuration.settings.webhook_secret.placeholder')}
                      helperText={t('integrations.detail.configuration.settings.webhook_secret.helper_text')}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <LoadingButton
                    type="submit"
                    onClick={onSubmit}
                    variant="contained"
                    loading={isSubmitting}
                    disabled={!isValid}
                  >
                    {t('common.update')}
                  </LoadingButton>
                </Box>
              </Stack>
            </FormProvider>
          )
      }
    </Card>
  );
}